import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ConfirmModal } from "../components/ConfirmModal";
import { Shell } from "../components/Shell";
import { CustomSelect } from "../components/CustomSelect";
import type { CustomSelectOption } from "../components/CustomSelect";
import {
  ApiError,
  funcionarios as funcsApi,
  tenants as tenantsApi,
} from "../lib/api";
import type {
  DependenciaSimple,
  FuncionarioCreate,
  FuncionarioCreateResponse,
  FuncionarioOut,
  FuncionarioRol,
  NivelJerarquico,
  Tenant,
  TipoDocFuncionario,
  TipoVinculacion,
} from "../lib/api";

const ROL_LABEL: Record<FuncionarioRol, string> = {
  admin_tenant: "Admin alcaldía",
  supervisor: "Supervisor",
  funcionario: "Funcionario",
  radicador: "Radicador",
};

const ROL_OPTS: ReadonlyArray<CustomSelectOption<FuncionarioRol>> = [
  { value: "funcionario", label: "Funcionario" },
  { value: "radicador", label: "Radicador" },
  { value: "supervisor", label: "Supervisor" },
  { value: "admin_tenant", label: "Admin alcaldía" },
];

export function TenantFuncionariosPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [items, setItems] = useState<FuncionarioOut[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<FuncionarioOut | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [pendingArchive, setPendingArchive] = useState<FuncionarioOut | null>(
    null,
  );
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [createdPwd, setCreatedPwd] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const t = await tenantsApi.get(id);
      setTenant(t);
      const r = await funcsApi.list(id, includeArchived);
      setItems(r.items);
      setTotal(r.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, includeArchived]);

  const toggleActivo = async (f: FuncionarioOut) => {
    if (!id) return;
    try {
      await funcsApi.update(id, f.id, { activo: !f.activo });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    }
  };

  const archivar = (f: FuncionarioOut) => {
    setPendingArchive(f);
  };

  const confirmArchivar = async () => {
    if (!id || !pendingArchive) return;
    setArchiveBusy(true);
    try {
      await funcsApi.archivar(id, pendingArchive.id);
      setPendingArchive(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setArchiveBusy(false);
    }
  };

  const restaurar = async (f: FuncionarioOut) => {
    if (!id) return;
    try {
      await funcsApi.restaurar(id, f.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    }
  };

  return (
    <Shell>
      <section className="page">
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap",
            marginBottom: 28,
          }}
        >
          <div>
            <div className="eyebrow">
              <Link to="/tenants">Alcaldías</Link> · {tenant?.slug} · Funcionarios
            </div>
            <h1>Funcionarios · {tenant?.nombre}</h1>
            <p className="lede" style={{ marginBottom: 0 }}>
              {total === 0
                ? "Sin funcionarios. Crea el primero."
                : `${total} funcionario${total === 1 ? "" : "s"} registrado${total === 1 ? "" : "s"}.`}
            </p>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <label
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                cursor: "pointer",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--ink-3)",
              }}
            >
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
              Ver archivados
            </label>
            <button
              type="button"
              className="cta"
              onClick={() => setShowCreate(true)}
              style={{ maxWidth: 240, width: "auto" }}
            >
              <span className="cta-inner">+ Nuevo funcionario</span>
            </button>
          </div>
        </div>

        {error && <div className="alert err">{error}</div>}

        {createdPwd && (
          <div className="alert warn" style={{ marginBottom: 18 }}>
            <strong>Password temporal generado para {createdPwd.email}:</strong>{" "}
            <code
              style={{
                fontFamily: "var(--font-mono)",
                background: "rgba(0,0,0,0.06)",
                padding: "2px 8px",
              }}
            >
              {createdPwd.password}
            </code>
            <div style={{ marginTop: 6, fontSize: 13 }}>
              Cópialo ya y envíaselo por canal seguro. <strong>No volverá a verse</strong>.
              El funcionario debe cambiarlo al primer login.
            </div>
            <button
              type="button"
              className="logout-btn"
              style={{ marginTop: 10 }}
              onClick={() => setCreatedPwd(null)}
            >
              Entendido, ocultar
            </button>
          </div>
        )}

        <div className="card">
          <div className="card-inner" style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--hairline-strong)" }}>
                  <Th>Nombre</Th>
                  <Th>Email</Th>
                  <Th>Rol</Th>
                  <Th>Dependencia</Th>
                  <Th>Estado</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ padding: 32, textAlign: "center", color: "var(--ink-3)" }}
                    >
                      Cargando…
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ padding: 32, textAlign: "center", color: "var(--ink-3)" }}
                    >
                      Sin funcionarios.
                    </td>
                  </tr>
                ) : (
                  items.map((f) => (
                    <tr
                      key={f.id}
                      style={{ borderBottom: "1px solid var(--hairline)" }}
                    >
                      <Td>
                        <div style={{ fontWeight: 600 }}>{f.nombre}</div>
                        {f.must_change_password && (
                          <div
                            style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 10,
                              color: "var(--warn)",
                            }}
                          >
                            password temporal pendiente
                          </div>
                        )}
                      </Td>
                      <Td mono>{f.email}</Td>
                      <Td>{ROL_LABEL[f.rol]}</Td>
                      <Td>{f.dependencia ?? "—"}</Td>
                      <Td>
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: f.activo ? "var(--ok)" : "var(--ink-4)",
                          }}
                        >
                          {f.activo ? "● Activo" : "Inactivo"}
                        </span>
                      </Td>
                      <Td>
                        <FuncionarioActionsPanel
                          f={f}
                          onEdit={() => setEditing(f)}
                          onToggle={() => void toggleActivo(f)}
                          onArchive={() => void archivar(f)}
                          onRestore={() => void restaurar(f)}
                        />
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showCreate && id && (
        <CreateFuncionarioModal
          tenantId={id}
          onClose={() => setShowCreate(false)}
          onCreated={(resp) => {
            setShowCreate(false);
            if (resp.generated_password) {
              setCreatedPwd({
                email: resp.email,
                password: resp.generated_password,
              });
            }
            void load();
          }}
        />
      )}
      {editing && id && (
        <EditFuncionarioModal
          tenantId={id}
          funcionario={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      <ConfirmModal
        open={pendingArchive !== null}
        title={
          pendingArchive ? `¿Archivar a ${pendingArchive.nombre}?` : ""
        }
        message="El funcionario quedará oculto del listado y no podrá entrar al sistema. Puedes restaurarlo después marcando 'Incluir archivados'."
        confirmLabel="Sí, archivar"
        variant="danger"
        busy={archiveBusy}
        onCancel={() => setPendingArchive(null)}
        onConfirm={() => void confirmArchivar()}
      />
    </Shell>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "14px 20px",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "var(--ink-3)",
        fontWeight: 500,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
}: {
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <td
      style={{
        padding: "12px 20px",
        fontFamily: mono ? "var(--font-mono)" : "var(--font-app)",
        fontSize: mono ? 13 : 14,
      }}
    >
      {children}
    </td>
  );
}

function CreateFuncionarioModal({
  tenantId,
  onClose,
  onCreated,
}: {
  tenantId: string;
  onClose: () => void;
  onCreated: (resp: FuncionarioCreateResponse) => void;
}) {
  const [form, setForm] = useState<FuncionarioCreate>({
    email: "",
    nombre: "",
    rol: "funcionario",
    dependencia_id: "",
    tipo_doc: null,
    numero_doc: "",
    cargo: "",
    nivel_jerarquico: null,
    tipo_vinculacion: null,
    telefono: "",
    fecha_posesion: null,
    acto_administrativo: "",
    password: "",
  });
  const [dependencias, setDependencias] = useState<DependenciaSimple[]>([]);
  const [usarPwdAuto, setUsarPwdAuto] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    funcsApi
      .listDependencias(tenantId)
      .then(setDependencias)
      .catch(() => setDependencias([]));
  }, [tenantId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: FuncionarioCreate = {
        email: form.email.trim().toLowerCase(),
        nombre: form.nombre.trim(),
        rol: form.rol,
        dependencia_id: form.dependencia_id || null,
        tipo_doc: form.tipo_doc || null,
        numero_doc: form.numero_doc?.trim() || null,
        cargo: form.cargo?.trim() || null,
        nivel_jerarquico: form.nivel_jerarquico || null,
        tipo_vinculacion: form.tipo_vinculacion || null,
        telefono: form.telefono?.trim() || null,
        fecha_posesion: form.fecha_posesion || null,
        acto_administrativo: form.acto_administrativo?.trim() || null,
      };
      if (!usarPwdAuto && form.password) body.password = form.password;
      const r = await funcsApi.create(tenantId, body);
      onCreated(r);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? typeof err.detail === "string"
            ? err.detail
            : err.message
          : "Error de red",
      );
    } finally {
      setBusy(false);
    }
  };

  const tipoDocOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Sin documento" },
    { value: "CC", label: "Cédula de ciudadanía" },
    { value: "CE", label: "Cédula de extranjería" },
    { value: "TI", label: "Tarjeta de identidad" },
    { value: "PA", label: "Pasaporte" },
    { value: "NIT", label: "NIT" },
    { value: "otro", label: "Otro" },
  ];
  const nivelOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Sin nivel" },
    { value: "directivo", label: "Directivo" },
    { value: "asesor", label: "Asesor" },
    { value: "profesional", label: "Profesional" },
    { value: "tecnico", label: "Técnico" },
    { value: "asistencial", label: "Asistencial" },
  ];
  const vinculacionOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Sin vinculación" },
    { value: "carrera", label: "Carrera administrativa" },
    { value: "libre_nombramiento", label: "Libre nombramiento" },
    { value: "provisional", label: "Provisional" },
    { value: "contrato_ops", label: "Contrato OPS" },
    { value: "trabajador_oficial", label: "Trabajador oficial" },
  ];
  const depOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Sin dependencia" },
    ...dependencias.map((d) => ({ value: d.id, label: d.nombre })),
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,22,26,0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "60px 20px",
        zIndex: 50,
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ maxWidth: 560, width: "100%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-inner">
          <div className="eyebrow">Alta · Funcionario</div>
          <h2 style={{ marginTop: 4 }}>Nuevo funcionario</h2>
          <p style={{ color: "var(--ink-3)", marginBottom: 18 }}>
            Recibirá un password temporal. Tendrá que cambiarlo al primer login.
          </p>

          {error && <div className="alert err">{error}</div>}

          <form onSubmit={submit}>
            <div className="form-row">
              <label className="label">Nombre completo</label>
              <input
                className="input"
                value={form.nombre}
                onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                required
                minLength={2}
              />
            </div>
            <div className="form-row">
              <label className="label">Email institucional</label>
              <input
                className="input"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
            </div>
            <div className="eyebrow" style={{ marginTop: 8 }}>
              Identificación
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 18 }}>
              <div className="form-row">
                <label className="label">Tipo de documento</label>
                <CustomSelect<string>
                  value={form.tipo_doc ?? ""}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      tipo_doc: (v || null) as TipoDocFuncionario | null,
                    }))
                  }
                  options={tipoDocOpts}
                  aria-label="Tipo de documento"
                />
              </div>
              <div className="form-row">
                <label className="label">Número de identificación</label>
                <input
                  className="input"
                  value={form.numero_doc ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, numero_doc: e.target.value }))
                  }
                  placeholder="1234567890"
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>
            </div>

            <div className="eyebrow" style={{ marginTop: 8 }}>Cargo y vinculación</div>
            <div className="form-row">
              <label className="label">Cargo</label>
              <input
                className="input"
                value={form.cargo ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, cargo: e.target.value }))}
                placeholder="Ej. Profesional Especializado"
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              <div className="form-row">
                <label className="label">Nivel jerárquico</label>
                <CustomSelect<string>
                  value={form.nivel_jerarquico ?? ""}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      nivel_jerarquico: (v || null) as NivelJerarquico | null,
                    }))
                  }
                  options={nivelOpts}
                  aria-label="Nivel jerárquico"
                />
              </div>
              <div className="form-row">
                <label className="label">Tipo de vinculación</label>
                <CustomSelect<string>
                  value={form.tipo_vinculacion ?? ""}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      tipo_vinculacion: (v || null) as TipoVinculacion | null,
                    }))
                  }
                  options={vinculacionOpts}
                  aria-label="Tipo de vinculación"
                />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              <div className="form-row">
                <label className="label" htmlFor="func-rol">Rol en MiPueblo</label>
                <CustomSelect<FuncionarioRol>
                  id="func-rol"
                  value={form.rol}
                  onChange={(v) => setForm((f) => ({ ...f, rol: v }))}
                  options={ROL_OPTS}
                  aria-label="Rol"
                />
              </div>
              <div className="form-row">
                <label className="label">Dependencia</label>
                <CustomSelect<string>
                  value={form.dependencia_id ?? ""}
                  onChange={(v) =>
                    setForm((f) => ({ ...f, dependencia_id: v || null }))
                  }
                  options={depOpts}
                  aria-label="Dependencia"
                />
              </div>
            </div>

            <div className="eyebrow" style={{ marginTop: 8 }}>Operativos</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              <div className="form-row">
                <label className="label">Teléfono</label>
                <input
                  className="input"
                  value={form.telefono ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, telefono: e.target.value }))
                  }
                  placeholder="3201234567"
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>
              <div className="form-row">
                <label className="label">Fecha de posesión</label>
                <input
                  className="input"
                  type="date"
                  value={form.fecha_posesion ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      fecha_posesion: e.target.value || null,
                    }))
                  }
                />
              </div>
            </div>
            <div className="form-row">
              <label className="label">Acto administrativo de nombramiento</label>
              <input
                className="input"
                value={form.acto_administrativo ?? ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    acto_administrativo: e.target.value,
                  }))
                }
                placeholder="Resolución 0123 de 2026"
              />
            </div>

            <div className="form-row" style={{ background: "var(--bone-warm)", padding: 14 }}>
              <label
                style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={usarPwdAuto}
                  onChange={(e) => setUsarPwdAuto(e.target.checked)}
                />
                <span>
                  Generar password aleatorio (recomendado — el sistema lo muestra una sola vez)
                </span>
              </label>
              {!usarPwdAuto && (
                <input
                  className="input"
                  type="text"
                  value={form.password ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, password: e.target.value }))
                  }
                  placeholder="Password temporal (min 8 chars)"
                  minLength={8}
                  required
                  style={{ marginTop: 10, fontFamily: "var(--font-mono)" }}
                />
              )}
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="logout-btn"
                onClick={onClose}
                disabled={busy}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="cta"
                disabled={busy || !form.email || !form.nombre}
                style={{ maxWidth: 240 }}
              >
                <span className="cta-inner">
                  {busy && <span className="spinner" aria-hidden />}
                  Crear funcionario
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============ Iconos de acción ============

function FuncionarioActionsPanel({
  f,
  onEdit,
  onToggle,
  onArchive,
  onRestore,
}: {
  f: FuncionarioOut;
  onEdit: () => void;
  onToggle: () => void;
  onArchive: () => void;
  onRestore: () => void;
}) {
  const archived = f.archivado_at != null;
  return (
    <div style={{ display: "inline-flex", gap: 2 }}>
      {!archived && (
        <IconActionBtn title="Editar funcionario" onClick={onEdit}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 4H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
          </svg>
        </IconActionBtn>
      )}
      {!archived && (
        <IconActionBtn
          title={f.activo ? "Desactivar" : "Reactivar"}
          onClick={onToggle}
        >
          {f.activo ? (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="6" y="5" width="4" height="14" />
              <rect x="14" y="5" width="4" height="14" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <polygon points="6 4 20 12 6 20 6 4" />
            </svg>
          )}
        </IconActionBtn>
      )}
      {archived ? (
        <IconActionBtn title="Restaurar archivado" onClick={onRestore}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 12a9 9 0 1 0 3-6.7" />
            <polyline points="3 4 3 10 9 10" />
          </svg>
        </IconActionBtn>
      ) : (
        <IconActionBtn title="Archivar" onClick={onArchive} danger>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="4" width="18" height="4" rx="1" />
            <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
            <line x1="10" y1="13" x2="14" y2="13" />
          </svg>
        </IconActionBtn>
      )}
    </div>
  );
}

function IconActionBtn({
  children,
  title,
  onClick,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      className={`equipo-icon-btn${danger ? " is-danger" : ""}`}
    >
      {children}
    </button>
  );
}

// ============ Modal de edición ============

function EditFuncionarioModal({
  tenantId,
  funcionario: f,
  onClose,
  onSaved,
}: {
  tenantId: string;
  funcionario: FuncionarioOut;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nombre, setNombre] = useState(f.nombre);
  const [rol, setRol] = useState<FuncionarioRol>(f.rol);
  const [dependenciaId, setDependenciaId] = useState(f.dependencia_id ?? "");
  const [tipoDoc, setTipoDoc] = useState<TipoDocFuncionario | null>(f.tipo_doc);
  const [numeroDoc, setNumeroDoc] = useState(f.numero_doc ?? "");
  const [cargo, setCargo] = useState(f.cargo ?? "");
  const [nivel, setNivel] = useState<NivelJerarquico | null>(f.nivel_jerarquico);
  const [vinculacion, setVinculacion] = useState<TipoVinculacion | null>(
    f.tipo_vinculacion,
  );
  const [telefono, setTelefono] = useState(f.telefono ?? "");
  const [fechaPosesion, setFechaPosesion] = useState(f.fecha_posesion ?? "");
  const [actoAdmin, setActoAdmin] = useState(f.acto_administrativo ?? "");
  const [dependencias, setDependencias] = useState<DependenciaSimple[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    funcsApi.listDependencias(tenantId).then(setDependencias).catch(() => setDependencias([]));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [tenantId, onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await funcsApi.update(tenantId, f.id, {
        nombre: nombre.trim(),
        rol,
        dependencia_id: dependenciaId || null,
        tipo_doc: tipoDoc,
        numero_doc: numeroDoc.trim() || null,
        cargo: cargo.trim() || null,
        nivel_jerarquico: nivel,
        tipo_vinculacion: vinculacion,
        telefono: telefono.trim() || null,
        fecha_posesion: fechaPosesion || null,
        acto_administrativo: actoAdmin.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setBusy(false);
    }
  };

  const tipoDocOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Sin documento" },
    { value: "CC", label: "Cédula de ciudadanía" },
    { value: "CE", label: "Cédula de extranjería" },
    { value: "TI", label: "Tarjeta de identidad" },
    { value: "PA", label: "Pasaporte" },
    { value: "NIT", label: "NIT" },
    { value: "otro", label: "Otro" },
  ];
  const nivelOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Sin nivel" },
    { value: "directivo", label: "Directivo" },
    { value: "asesor", label: "Asesor" },
    { value: "profesional", label: "Profesional" },
    { value: "tecnico", label: "Técnico" },
    { value: "asistencial", label: "Asistencial" },
  ];
  const vincOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Sin vinculación" },
    { value: "carrera", label: "Carrera administrativa" },
    { value: "libre_nombramiento", label: "Libre nombramiento" },
    { value: "provisional", label: "Provisional" },
    { value: "contrato_ops", label: "Contrato OPS" },
    { value: "trabajador_oficial", label: "Trabajador oficial" },
  ];
  const depOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Sin dependencia" },
    ...dependencias.map((d) => ({ value: d.id, label: d.nombre })),
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,22,26,0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "60px 20px",
        zIndex: 50,
        overflowY: "auto",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="card"
        style={{ maxWidth: 560, width: "100%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-inner">
          <div className="eyebrow">Funcionario · Editar</div>
          <h2 style={{ marginTop: 4 }}>{f.nombre}</h2>
          <p style={{ color: "var(--ink-3)", marginBottom: 18 }}>{f.email}</p>

          {error && <div className="alert err">{error}</div>}

          <form onSubmit={submit}>
            <div className="form-row">
              <label className="label">Nombre completo</label>
              <input
                className="input"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                minLength={2}
              />
            </div>

            <div className="eyebrow" style={{ marginTop: 8 }}>Identificación</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 18 }}>
              <div className="form-row">
                <label className="label">Tipo de documento</label>
                <CustomSelect<string>
                  value={tipoDoc ?? ""}
                  onChange={(v) => setTipoDoc((v || null) as TipoDocFuncionario | null)}
                  options={tipoDocOpts}
                  aria-label="Tipo de documento"
                />
              </div>
              <div className="form-row">
                <label className="label">Número</label>
                <input
                  className="input"
                  value={numeroDoc}
                  onChange={(e) => setNumeroDoc(e.target.value)}
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>
            </div>

            <div className="eyebrow" style={{ marginTop: 8 }}>Cargo y vinculación</div>
            <div className="form-row">
              <label className="label">Cargo</label>
              <input className="input" value={cargo} onChange={(e) => setCargo(e.target.value)} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              <div className="form-row">
                <label className="label">Nivel jerárquico</label>
                <CustomSelect<string>
                  value={nivel ?? ""}
                  onChange={(v) => setNivel((v || null) as NivelJerarquico | null)}
                  options={nivelOpts}
                  aria-label="Nivel"
                />
              </div>
              <div className="form-row">
                <label className="label">Tipo de vinculación</label>
                <CustomSelect<string>
                  value={vinculacion ?? ""}
                  onChange={(v) => setVinculacion((v || null) as TipoVinculacion | null)}
                  options={vincOpts}
                  aria-label="Vinculación"
                />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              <div className="form-row">
                <label className="label">Rol</label>
                <CustomSelect<FuncionarioRol>
                  value={rol}
                  onChange={(v) => setRol(v)}
                  options={ROL_OPTS}
                  aria-label="Rol"
                />
              </div>
              <div className="form-row">
                <label className="label">Dependencia</label>
                <CustomSelect<string>
                  value={dependenciaId}
                  onChange={(v) => setDependenciaId(v)}
                  options={depOpts}
                  aria-label="Dependencia"
                />
              </div>
            </div>

            <div className="eyebrow" style={{ marginTop: 8 }}>Operativos</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
              <div className="form-row">
                <label className="label">Teléfono</label>
                <input
                  className="input"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  style={{ fontFamily: "var(--font-mono)" }}
                />
              </div>
              <div className="form-row">
                <label className="label">Fecha de posesión</label>
                <input
                  className="input"
                  type="date"
                  value={fechaPosesion}
                  onChange={(e) => setFechaPosesion(e.target.value)}
                />
              </div>
            </div>
            <div className="form-row">
              <label className="label">Acto administrativo</label>
              <input
                className="input"
                value={actoAdmin}
                onChange={(e) => setActoAdmin(e.target.value)}
              />
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button type="button" className="logout-btn" onClick={onClose} disabled={busy}>
                Cancelar
              </button>
              <button
                type="submit"
                className="cta"
                disabled={busy || !nombre.trim()}
                style={{ maxWidth: 240 }}
              >
                <span className="cta-inner">
                  {busy && <span className="spinner" aria-hidden />}
                  Guardar cambios
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
